// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ReportComment from "./ReportComment/ReportComment";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import { sortByDate } from "src/utils/date/dateHelper";

// Constants
import { patientConditionTypes } from "src/constants/enums";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const ReportComments = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { outline } = getSurfaceTokens(theme);

  // Same reasoning as ModalCustomConfirmBlack -- a solid black header
  // disappears against a dark page, so flip to a light surface in dark mode.
  const headerBackground = isDark ? "#F5F6F8" : "#000";
  const headerColor = isDark ? "#12141C" : "#FFF";

  const { commentData } = useContext(ReportSectionContext);

  const sortedPatientConditions = useMemo(
    () =>
      patientConditionTypes
        .map((condition) => condition)
        .sort((a, b) => t(a).localeCompare(t(b))),
    [t]
  );

  const sortedComments = useMemo(
    () => sortByDate(commentData, "creationDate"),
    [commentData]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "4px",
        border: `1px solid ${outline}`,
      }}
    >
      <Typography
        sx={{
          background: headerBackground,
          color: headerColor,
          padding: "8px",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        {t("Report Comments")}
      </Typography>

      {sortedComments?.length > 0 ? (
        <Box sx={{ display: "flex", width: "100%" }}>
          <Box
            id="comments-list"
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              padding: "8px",
              gap: "4px",
              maxHeight: "152px",
              overflowY: "auto",
            }}
          >
            {sortedComments.map((comment, index) => (
              <ReportComment
                key={comment.id}
                comment={comment}
                prevConditions={
                  index < sortedComments.length - 1
                    ? sortedComments[index + 1]?.conditions
                    : []
                }
              />
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              width: "fit-content",
              borderLeft: `1px solid ${outline}`,
              p: "8px 12px",
              gap: "4px",
            }}
          >
            {sortedPatientConditions.map((condition, index) => (
              <Box key={index} sx={{ display: "flex" }}>
                <CustomCheckbox
                  id={`condition-checkbox-${condition}-${index}`}
                  checked={sortedComments[0]?.conditions?.includes(condition)}
                  disabled
                  onClick={() => {
                    // Do nothing
                  }}
                  checkedFill="#5B6D7A"
                  uncheckedStroke="#5B6D7A"
                />
                <Typography sx={{ ml: "8px", fontSize: "14px" }}>
                  {t(condition)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography
          sx={{ textAlign: "center", padding: "20px", fontSize: "14px" }}
        >
          {t("No comments found.")}
        </Typography>
      )}
    </Box>
  );
};

export default ReportComments;
